// src/PagesOfCommon/wiki/index.en.js
import React, { useMemo, useRef, useState } from "react";
import s from "./Wiki.module.css";

/** Small utilities */
const Kbd = ({ children }) => <kbd className={s.kbd}>{children}</kbd>;

const apiBaseUrl = (process.env.REACT_APP_API_URL || process.env.API_URL || "").replace(/\/$/, "");
const API_V3_DOCS_URL = `${apiBaseUrl}/api/v3/docs`;

const CopyBtn = ({ getText, title = "Copy" }) => {
    const [ok, setOk] = useState(false);
    return (
        <button
            type="button"
            className={s.copyBtn}
            title={title}
            onClick={async () => {
                try {
                    await navigator.clipboard.writeText(getText());
                    setOk(true);
                    setTimeout(() => setOk(false), 1200);
                } catch (e) {
                    console.error("Copy error:", e);
                }
            }}
        >
            {ok ? "✓" : "⧉"}
        </button>
    );
};

const Code = ({ lang = "json", children }) => {
    const pre = useRef(null);
    const getText = () => (pre.current ? pre.current.innerText : "");
    return (
        <div className={s.code}>
            <div className={s.codeBar}>
                <span className={s.codeLang}>{lang}</span>
                <CopyBtn getText={getText} />
            </div>
            <pre ref={pre}>
        <code>{children}</code>
      </pre>
        </div>
    );
};

/** cURL builders */
const buildCurlPOST = (base, path, headers, body) => {
    const lines = [];
    lines.push(`curl -X POST '${base}${path}' \\`);
    headers.forEach((h) => lines.push(`  -H '${h.name}: ${h.value}' \\`));
    lines.push(`  -d '${String(body).replace(/'/g, "'\\''")}'`);
    return lines.join("\n");
};
const buildCurlGET = (base, path, headers) => {
    const lines = [];
    lines.push(`curl -X GET '${base}${path}' \\`);
    headers.forEach((h, i) => {
        const isLast = i === headers.length - 1;
        lines.push(`  -H '${h.name}: ${h.value}'${isLast ? "" : " \\"}`);
    });
    return lines.join("\n");
};

/** Providers config (for merchants) */
const PROVIDERS = {
    /* === CRAFT (Paycraft) === */
    craft: {
        key: "craft",
        label: "Craft",
        baseUrlProd: "https://api.e-clear.cc/api",
        baseUrlTest: "https://api.test.e-clear.cc/api",
        authHelp: (
            <span>
                <Kbd>X-Merchant-UUID</Kbd> + <Kbd>X-Merchant-Key</Kbd>
            </span>
        ),
        supportsStatus: true,
        headers: [
            { name: "Content-Type", value: "application/json" },
            { name: "X-Merchant-UUID", value: "<YOUR-UUID>" },
            { name: "X-Merchant-Key", value: "<YOUR-KEY>" },
        ],
        methods: [
            {
                key: "h2h",
                label: "H2H (QR code)",
                path: "/craft/payment",
                bodyWithIp: null,
                bodyWithoutIp: () => `{
  "client": "+79001234567",
  "product": "Product name",
  "sum": 1000,
  "h2h": true,
  "externalReference": "order-123",
  "callbackUrl": "https://merchant.example/callback"
}`,
                successManual: `{
  "transactionId": 2317170,
  "trxUuid": "c09d5d68-8065-4770-...",
  "processingId": 1030674,
  "amount": 1000,
  "currency": "RUB",
  "externalRefs": {
    "payload": "https://v1.npay.kg/#00020101...",
    "expire-at": "2025-12-24T09:03:04.409Z"
  },
  "createdAt": "2025-12-24T08:54:04+00:00",
  "expiresAt": "2025-12-24T09:24:04+00:00"
}`,
            },
            {
                key: "widget",
                label: "Widget (redirect)",
                path: "/craft/payment",
                bodyWithIp: null,
                bodyWithoutIp: () => `{
  "client": "+79001234567",
  "product": "Product name",
  "sum": 1000,
  "externalReference": "order-123",
  "redirectUrlSuccess": "https://merchant.example/success",
  "redirectUrlError": "https://merchant.example/decline",
  "callbackUrl": "https://merchant.example/callback"
}`,
                successManual: `{
  "transactionId": 2316512,
  "trxUuid": "3a3b8b56-5b8a-4a4e-...",
  "processingId": "abc123...",
  "amount": 1000,
  "currency": "RUB",
  "redirectUrl": "https://paycraftpayment.com/checkout/..."
}`,
            },
        ],
        status: {
            path: function (id) {
                return "/craft/status/" + (id || "<transactionId>");
            },
            success: `{
  "transactionId": 2317170,
  "trxUuid": "c09d5d68-8065-4770-...",
  "externalReference": "order-123",
  "amount": 1000,
  "currency": "RUB",
  "pspState": "SUCCESS",
  "synced": true
}`,
        },
        callbackSample: `{
  "transactionId": 2317170,
  "externalReference": "order-123",
  "state": "SUCCESS",
  "amount": 1000,
  "currency": "RUB"
}`,
    },

    /* === HYDRA === */
    hydra: {
        key: "hydra",
        label: "Hydra",
        baseUrlProd: "https://api.e-clear.cc/api",
        baseUrlTest: "https://api.test.e-clear.cc/api",
        authHelp: (
            <span>
        <Kbd>X-Merchant-UUID</Kbd> + <Kbd>X-Merchant-Key</Kbd>
      </span>
        ),
        supportsStatus: true,
        headers: [
            { name: "Content-Type", value: "application/json" },
            { name: "X-Merchant-UUID", value: "<YOUR-UUID>" },
            { name: "X-Merchant-Key", value: "<YOUR-KEY>" },
        ],
        methods: [
            {
                key: "p2p",
                label: "Create payment (P2P)",
                path: "/hydra/p2p",
                bodyWithIp: (label) => `{
  "amount": 1000,
  "currency": "RUB",
  "externalReference": "order-123",
  "description": "${label} test",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline",
  "callbackUrl": "https://merchant.example/callback",
  "customerIp": "203.0.113.17"
}`,
                bodyWithoutIp: (label) => `{
  "amount": 1000,
  "currency": "RUB",
  "externalReference": "order-123",
  "description": "${label} test",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline",
  "callbackUrl": "https://merchant.example/callback"
}`,
                successManual: `{
  "amount": 1000,
  "currency": "RUB",
  "externalRefs": [ /* array of payment details */ ],
  "createdAt": "2025-10-02T21:28:24+00:00",
  "expiresAt": "2025-10-02T21:43:24+00:00",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline",
  "trxUuid": "3a3b8b56-5b8a-4a4e-8b2b-1f6e8d2a1cde",
  "localTransactionId": 2316512,
  "transactionId": 2316512,
  "hydraId": "5849...8bd9"
}`,
                successLink: `{
  "redirectUrl": "https://hydra.example/checkout/abc123",
  "trxUuid": "3a3b8b56-5b8a-4a4e-8b2b-1f6e8d2a1cde",
  "localTransactionId": 2316512,
  "transactionId": 2316512,
  "hydraId": "5849...8bd9",
  "amount": 1000,
  "currency": "RUB",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline"
// or
// "paymentUrl": "https://pay.e-clear.cc/<uuid>"
}`,
            },
            {
                key: "sbp",
                label: "Create payment (SBP)",
                path: "/hydra/sbp",
                bodyWithIp: (label) => `{
  "amount": 1000,
  "currency": "RUB",
  "externalReference": "order-123",
  "description": "${label} test",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline",
  "callbackUrl": "https://merchant.example/callback",
  "customerIp": "203.0.113.17"
}`,
                bodyWithoutIp: (label) => `{
  "amount": 1000,
  "currency": "RUB",
  "externalReference": "order-123",
  "description": "${label} test",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline",
  "callbackUrl": "https://merchant.example/callback"
}`,
                successManual: `{
  "amount": 1000,
  "currency": "RUB",
  "externalRefs": [ /* array of payment details */ ],
  "createdAt": "2025-10-02T21:28:24+00:00",
  "expiresAt": "2025-10-02T21:43:24+00:00",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline",
  "trxUuid": "3a3b8b56-5b8a-4a4e-8b2b-1f6e8d2a1cde",
  "localTransactionId": 2316512,
  "transactionId": 2316512,
  "hydraId": "5849...8bd9"
}`,
                successLink: `{
  "redirectUrl": "https://hydra.example/checkout/abc123",
  "trxUuid": "3a3b8b56-5b8a-4a4e-8b2b-1f6e8d2a1cde",
  "localTransactionId": 2316512,
  "transactionId": 2316512,
  "hydraId": "5849...8bd9",
  "amount": 1000,
  "currency": "RUB",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline"
// or
// "paymentUrl": "https://pay.e-clear.cc/<uuid>"
}`,
            },
        ],
        status: {
            path: function (id) {
                return "/hydra/status/" + (id || "<transactionId>");
            },
            success: `{
  "transactionId": 2316512,
  "externalReference": "order-123",
  "amount": 1000,
  "currency": "RUB",
  "processingId": "5849...8bd9",
  "hydraStatus": "COMPLETED",
  "hydraState": "COMPLETED",
  "synced": true,
  "createdAt": "2025-10-02T21:28:24+00:00"
}`,
        },
        callbackSample: `{
  "transactionId": 2316512,
  "externalReference": "order-123",
  "state": "COMPLETED",
  "amount": 1000,
  "currency": "RUB",
  "processingId": "5849...8bd9",
  "createdAt": "2025-10-02T21:28:24+00:00"
}`,
    },

    /* === INTERNAL (Internal methods) === */
    internal: {
        key: "internal",
        label: "Internal",
        baseUrlProd: "https://api.e-clear.cc/api",
        baseUrlTest: "https://api.test.e-clear.cc/api",
        authHelp: (
            <span>
                <Kbd>X-Merchant-UUID</Kbd> + <Kbd>X-Merchant-Key</Kbd>
            </span>
        ),
        supportsStatus: false,
        headers: [
            { name: "Content-Type", value: "application/json" },
            { name: "X-Merchant-UUID", value: "<YOUR-UUID>" },
            { name: "X-Merchant-Key", value: "<YOUR-KEY>" },
        ],
        methods: [
            {
                key: "terminals",
                label: "Terminals list",
                path: "/v1",
                bodyWithIp: null,
                bodyWithoutIp: () => `{
  "method": { "type": "balance", "direction": "terminals" }
}`,
                successManual: `{
  "terminals": [
    {
      "terminalId": 123,
      "name": "Terminal SBP",
      "currency": "RUB",
      "balance": 15000.50,
      "transactionBalance": 1500050,
      "transactionBalanceFloat": 15000.50,
      "settlementBalance": 500000,
      "settlementBalanceFloat": 5000.00,
      "actualBalance": 1000050,
      "actualBalanceFloat": 10000.50
    },
    {
      "terminalId": 456,
      "name": "Terminal Card",
      "currency": "RUB",
      "balance": 8500.00,
      "transactionBalance": 850000,
      "transactionBalanceFloat": 8500.00,
      "settlementBalance": 200000,
      "settlementBalanceFloat": 2000.00,
      "actualBalance": 650000,
      "actualBalanceFloat": 6500.00
    }
  ]
}`,
            },
            {
                key: "terminal_balance",
                label: "Terminal balance",
                path: "/v1",
                bodyWithIp: null,
                bodyWithoutIp: () => `{
  "method": { "type": "balance", "direction": "terminal" },
  "terminalId": 123
}`,
                successManual: `{
  "terminal": {
    "terminalId": 123,
    "name": "Terminal SBP",
    "currency": "RUB",
    "balance": 15000.50,
    "transactionBalance": 1500050,
    "transactionBalanceFloat": 15000.50,
    "settlementBalance": 500000,
    "settlementBalanceFloat": 5000.00,
    "actualBalance": 1000050,
    "actualBalanceFloat": 10000.50
  }
}`,
            },
            {
                key: "balance_total",
                label: "Total balance",
                path: "/v1",
                bodyWithIp: null,
                bodyWithoutIp: () => `{
  "method": { "type": "balance" }
}`,
                successManual: `{
  "balance": [
    { "currency": "RUB", "value": 23500.50 }
  ],
  "providers": {
    "a1b2c3d4-e5f6-...": [
      { "currency": "RUB", "value": 15000.50 }
    ],
    "b2c3d4e5-f6g7-...": [
      { "currency": "RUB", "value": 8500.00 }
    ]
  },
  "balances": {
    "items": [
      {
        "terminal_id": 123,
        "terminal_name": "Terminal SBP",
        "merchant_settings_id": 456,
        "transactionBalance": 1500050,
        "transactionBalanceFloat": 15000.50,
        "settlementBalance": 500000,
        "settlementBalanceFloat": 5000.00,
        "actualBalance": 1000050,
        "actualBalanceFloat": 10000.50
      },
      {
        "terminal_id": 789,
        "terminal_name": "Terminal Card",
        "merchant_settings_id": 101,
        "transactionBalance": 850000,
        "transactionBalanceFloat": 8500.00,
        "settlementBalance": 200000,
        "settlementBalanceFloat": 2000.00,
        "actualBalance": 650000,
        "actualBalanceFloat": 6500.00
      }
    ],
    "totals": {
      "transactionBalance": 2350050,
      "transactionBalanceFloat": 23500.50,
      "settlementBalance": 700000,
      "settlementBalanceFloat": 7000.00,
      "actualBalance": 1650050,
      "actualBalanceFloat": 16500.50
    }
  }
}`,
            },
        ],
        callbackSample: null,
    },

    /* === NPAY — QR payments (Kyrgyzstan, npay.kg) === */
    npay: {
        key: "npay",
        label: "NPay (QR)",
        baseUrlProd: "https://api.e-clear.cc/api",
        baseUrlTest: "https://api.test.e-clear.cc/api",
        authHelp: (
            <span>
                <Kbd>X-Merchant-UUID</Kbd> + <Kbd>X-Merchant-Key</Kbd>
            </span>
        ),
        supportsStatus: true,
        headers: [
            { name: "Content-Type", value: "application/json" },
            { name: "X-Merchant-UUID", value: "<YOUR-UUID>" },
            { name: "X-Merchant-Key", value: "<YOUR-KEY>" },
        ],
        methods: [
            {
                key: "payment",
                label: "Create payment",
                path: "/npay/payments",
                bodyWithIp: null,
                bodyWithoutIp: function () {
                    return `{
  "amount": 100,
  "external_reference": "order-123",
  "comment": "Order #123 payment",
  "redirect_url": "https://merchant.example/success",
  "callback_url": "https://merchant.example/callback",
  "amount_can_be_changed": false,
  "expire_at": "2026-06-25T18:00:00+03:00"
}`;
                },
                successManual: `{
  "ok": true,
  "transactionId": 281,
  "npayGuid": "6a4cd87f-d1c8-4e55-8928-17cd37ce1a07",
  "payload": "https://pay.namba.kg/#00020101...6304XXXX",
  "paymentUrl": "https://pay.namba.kg/#00020101...6304XXXX",
  "qrBase64": "iVBORw0KGgoAAAANSUhEUg...",
  "qrDataUri": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUg...",
  "paymentPageUrl": "https://my.e-clear.cc/npay/3f8c9a1b...d21",
  "amount": 100,
  "amountRub": 100,
  "amountKgs": 112.45,
  "rate": 1.1245,
  "currency": "RUB",
  "expireAt": "2026-06-25T18:00:00+03:00",
  "status": "PROCESSING",
  "request_id": "abc123..."
}`,
                successLink: null,
            },
            {
                key: "payment_status",
                label: "Payment status",
                path: "/npay/payments/<transactionId>",
                bodyWithIp: null,
                bodyWithoutIp: null,
                successManual: `{
  "ok": true,
  "transactionId": 281,
  "npayGuid": "6a4cd87f-d1c8-4e55-8928-17cd37ce1a07",
  "status": "SUCCESS",
  "providerStatus": null,
  "amount": 100,
  "currency": "RUB",
  "request_id": "abc123..."
}`,
                successLink: null,
            },
            {
                key: "page",
                label: "Payment page data",
                path: "/npay/page/<token>",
                bodyWithIp: null,
                bodyWithoutIp: null,
                successManual: `{
  "ok": true,
  "status": "PROCESSING",
  "payload": "https://pay.namba.kg/#00020101...6304XXXX",
  "qrBase64": "iVBORw0KGgoAAAANSUhEUg...",
  "qrDataUri": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUg...",
  "amountRub": 100,
  "amountKgs": 112.45,
  "currency": "RUB",
  "expireAt": "2026-06-25T18:00:00+03:00",
  "expireAtTs": 1782313080,
  "request_id": "abc123..."
}`,
                successLink: null,
            },
            {
                key: "refund",
                label: "Create refund",
                path: "/npay/refunds",
                bodyWithIp: null,
                bodyWithoutIp: function () {
                    return `{
  "transaction_id": 281,
  "amount": 100,
  "comment": "Refund on customer request",
  "callback_url": "https://merchant.example/refund-callback"
}`;
                },
                successManual: `{
  "ok": true,
  "transactionId": 282,
  "parentTransactionId": 281,
  "npayRefundGuid": "b2c3d4e5-f6a7-8901-bcde-f12345678901",
  "amount": 100,
  "amountRub": 100,
  "amountKgs": 112.45,
  "rate": 1.1245,
  "currency": "RUB",
  "status": "pending",
  "request_id": "abc123..."
}`,
                successLink: null,
            },
            {
                key: "refund_status",
                label: "Refund status",
                path: "/npay/refunds/<transactionId>",
                bodyWithIp: null,
                bodyWithoutIp: null,
                successManual: `{
  "ok": true,
  "transactionId": 282,
  "status": "SUCCESS",
  "providerStatus": "COMPLETED",
  "request_id": "abc123..."
}`,
                successLink: null,
            },
        ],
        status: {
            path: function (id) {
                return "/npay/payments/" + (id || "<transactionId>");
            },
            success: `{
  "ok": true,
  "transactionId": 281,
  "npayGuid": "6a4cd87f-d1c8-4e55-8928-17cd37ce1a07",
  "status": "SUCCESS",
  "providerStatus": null,
  "amount": 100,
  "currency": "RUB",
  "request_id": "abc123..."
}`,
        },
        callbackSample: `// POST to your callback_url when the payment status changes.
// Amount is in RUB (the currency of your payment), same as on creation.
{
  "transactionId": 281,
  "externalReference": "order-123",
  "state": "SUCCESS",
  "providerStatus": "COMPLETED",
  "amount": 100,
  "amountRub": 100,
  "currency": "RUB",
  "createdAt": "2026-06-25T10:30:00+03:00"
}

// state: SUCCESS | DECLINED | PENDING`,
        webhookSample: `// This is a webhook FROM NPay TO our server — handled by us, nothing to do on your side.
// Shown for reference only. You receive a callback instead (see left).
// POST /webhooks/npay/{token}
// Type PAYMENT_ORDER:
{
  "type": "PAYMENT_ORDER",
  "data": {
    "externalId": "281",
    "status": "COMPLETED",
    "guid": "6a4cd87f-d1c8-4e55-8928-17cd37ce1a07",
    "amount": 11245
  }
}

// Type REFUND_ORDER:
{
  "type": "REFUND_ORDER",
  "data": {
    "externalId": "282",
    "status": "COMPLETED",
    "guid": "b2c3d4e5-f6a7-8901-bcde-f12345678901",
    "amount": 11245
  }
}`,
        errorSamples: `// 400 — missing/invalid fields
{
  "ok": false,
  "error": { "code": "bad_request", "message": "amount is required and must be numeric" }
}

// 400 — no NPay settings for this merchant
{
  "ok": false,
  "error": { "code": "bad_request", "message": "No NPay settings found for this merchant." }
}

// 403 — invalid credentials
{
  "ok": false,
  "error": { "code": "access_denied", "message": "Merchant auth failed." }
}

// 404 — payment page not found (invalid/expired token)
{
  "ok": false,
  "error": { "code": "not_found", "message": "Payment not found" }
}

// 500 — NPay provider error
{
  "ok": false,
  "error": { "code": "internal_error", "message": "NPay createOneTimeLink failed: ..." }
}`,
    },
};

/** Page */
const MerchantsDocsEn = () => {
    const [providerKey, setProviderKey] = useState("hydra");
    const [methodKey, setMethodKey] = useState("p2p");

    const provider = PROVIDERS[providerKey];

    const method = useMemo(() => {
        const list = provider && provider.methods ? provider.methods : [];
        let found = null;
        for (let i = 0; i < list.length; i++) {
            if (list[i].key === methodKey) {
                found = list[i];
                break;
            }
        }
        if (!found && list.length > 0) found = list[0];
        return found;
    }, [providerKey, methodKey, provider]);

    const createCurlWithIp = useMemo(() => {
        if (!provider || !method || !method.bodyWithIp) return "";
        const base = provider.baseUrlTest;
        const bodyStr = method.bodyWithIp(method.label) || "";
        return buildCurlPOST(base, method.path, provider.headers, bodyStr);
    }, [provider, method]);

    const createCurlWithoutIp = useMemo(() => {
        if (!provider || !method || !method.bodyWithoutIp) return "";
        const base = provider.baseUrlTest;
        const bodyStr = method.bodyWithoutIp(method.label) || "";
        return buildCurlPOST(base, method.path, provider.headers, bodyStr);
    }, [provider, method]);

    const statusCurl = useMemo(() => {
        if (!provider || !provider.supportsStatus) return "";
        const base = provider.baseUrlTest;
        const path = provider.status.path("<id>");
        return buildCurlGET(base, path, provider.headers);
    }, [provider]);

    // Public GET of the NPay payment page — WITHOUT auth headers.
    const npayPageCurl = useMemo(() => {
        if (!provider || providerKey !== "npay") return "";
        return buildCurlGET(provider.baseUrlTest, "/npay/page/<token>", []);
    }, [provider, providerKey]);


    return (
        <div className={s.page}>
            <main className={s.container}>
                <section className={s.hero}>
                    <h1 className={s.h1}>API for merchants</h1>
                    <p className={s.lead}>
                        Payments: <strong>Craft</strong> (QR),{" "}
                        <strong>Hydra</strong> (P2P, SBP),{" "}
                        <strong>NPay</strong> (QR, Kyrgyzstan).
                        Payouts: <strong>TKB</strong>, <strong>FINS</strong>.
                        Card check: <strong>Antifraud</strong>.
                    </p>
                    <div className={s.apiV3Banner}>
                        <div>
                            <strong>Unified API v3</strong>
                            <span>
                                The recommended integration: all methods, consistent formats and request testing.
                            </span>
                        </div>
                        <a className={s.apiV3Link} href={API_V3_DOCS_URL}>
                            Open interactive documentation
                        </a>
                    </div>
                    <div className={s.meta}>
            <span>
              Prod: <code>{provider ? provider.baseUrlProd : ""}</code>
            </span>
                        <span>
              Test: <code>{provider ? provider.baseUrlTest : ""}</code>
            </span>
                        <span>
              Content-Type: <code>application/json</code>
            </span>
                        <span>Auth: {provider ? provider.authHelp : null}</span>
                    </div>

                    {providerKey === "craft" && (
                        <div className={s.note}>
                            <strong>Craft</strong> — accepting payments via QR code.
                        </div>
                    )}
                    {providerKey === "fins" && (
                        <div className={s.note}>
                            Idempotency is handled by the adapter — you don&apos;t need to
                            send a separate <Kbd>X-Idempotency-Key</Kbd>. For your own
                            duplicates use the <code>external_reference</code> field.
                        </div>
                    )}
                    {providerKey === "tkb" && (
                        <div className={s.note}>
                            All operations go through a single POST{" "}
                            <code>/api/v1</code> with <code>method.type</code>.
                            For TKB payouts it&apos;s enough to pass{" "}
                            <code>card.number</code>; <code>exp</code>/<code>cvv</code> are
                            not required.
                        </div>
                    )}
                    {providerKey === "npay" && (
                        <div className={s.note}>
                            QR payments (Kyrgyzstan, <strong>npay.kg</strong>).
                            You send the amount <strong>in rubles</strong>{" "}
                            (<code>amount</code>, <code>currency: RUB</code>) — we
                            automatically convert it to soms (KGS) at the current rate
                            and build the QR. The customer pays by scanning the QR in
                            their bank app. You get the result on your{" "}
                            <code>callback_url</code>; the status is also available via{" "}
                            <code>GET /npay/payments/{"<transactionId>"}</code>.
                        </div>
                    )}
                </section>

                {/* Provider switch */}
                <nav className={s.tabs} role="tablist" aria-label="Integration">
                    {Object.keys(PROVIDERS).map((k) => {
                        const active = providerKey === k;
                        const classes = active ? `${s.tab} ${s.tabActive}` : s.tab;
                        return (
                            <button
                                key={k}
                                role="tab"
                                aria-selected={active}
                                className={classes}
                                onClick={() => {
                                    setProviderKey(k);
                                    const list =
                                        PROVIDERS[k] && PROVIDERS[k].methods
                                            ? PROVIDERS[k].methods
                                            : [];
                                    const first = list.length > 0 ? list[0].key : null;
                                    if (first) setMethodKey(first);
                                }}
                            >
                                {PROVIDERS[k].label}
                            </button>
                        );
                    })}
                </nav>

                {/* Method switch */}
                <nav className={s.tabs} role="tablist" aria-label="Methods">
                    {(provider && provider.methods ? provider.methods : []).map((m) => {
                        const active = method && method.key === m.key;
                        const classes = active ? `${s.tab} ${s.tabActive}` : s.tab;
                        return (
                            <button
                                key={m.key}
                                role="tab"
                                aria-selected={active}
                                className={classes}
                                onClick={() => setMethodKey(m.key)}
                            >
                                {m.label}
                            </button>
                        );
                    })}
                </nav>

                {/* How it works */}
                <section className={s.card}>
                    <h2 className={s.h2}>How it works</h2>
                    {providerKey === "craft" ? (
                        <ol className={s.ol}>
                            <li>
                                <code>POST /craft/payment</code> — create payment.
                            </li>
                            <li>
                                <strong>H2H</strong> (<code>h2h: true</code>): returns QR link in <code>externalRefs.payload</code>.
                            </li>
                            <li>
                                <strong>Widget</strong> (without <code>h2h</code>): returns <code>redirectUrl</code> to checkout page.
                            </li>
                            <li>
                                <code>GET /craft/status/{"{transactionId}"}</code> — check status.
                            </li>
                            <li>
                                Callback is sent to <code>callbackUrl</code> on status change.
                            </li>
                        </ol>
                    ) : providerKey === "hydra" ? (
                        <ol className={s.ol}>
                            <li>
                                Send <code>POST {method ? method.path : ""}</code> with body and
                                headers.
                            </li>
                            <li>
                                If <code>customerIp</code> is provided — we return{" "}
                                <code>externalRefs</code> and identifiers.
                            </li>
                            <li>
                                Without <code>customerIp</code> — we return a payment link (
                                <code>redirectUrl</code> or <code>paymentUrl</code>).
                            </li>
                            <li>
                                Status check:{" "}
                                <code>
                                    GET {provider ? provider.status.path("{transactionId}") : ""}
                                </code>
                                .
                            </li>
                            <li>
                                If <code>callbackUrl</code> is provided — we send a server
                                callback.
                            </li>
                        </ol>
                    ) : providerKey === "tkb" ? (
                        <ol className={s.ol}>
                            <li>
                                Single route: <code>POST /api/v1</code>.
                            </li>
                            <li>
                                In the request specify <code>method.type</code>:
                                <ul className={s.ul}>
                                    <li>
                                        <code>payout</code> — create payout (provider{" "}
                                        <code>tkb</code>).
                                    </li>
                                    <li>
                                        <code>balance</code> — get merchant balance.
                                    </li>
                                    <li>
                                        <code>status</code> — get status(es) by{" "}
                                        <code>transactionId</code> or{" "}
                                        <code>externalReference</code>.
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Callback results are sent to your <code>callbackUrl</code> (if
                                specified).
                            </li>
                        </ol>
                    ) : providerKey === "npay" ? (
                        <ol className={s.ol}>
                            <li>
                                Create a payment: <code>POST /npay/payments</code> with the
                                amount in rubles. The response contains{" "}
                                <code>transactionId</code>, a ready-made QR
                                (<code>qrDataUri</code> / <code>qrBase64</code>) and the
                                payment-page link <code>paymentPageUrl</code>.
                            </li>
                            <li>
                                Show the QR to the customer in one of two ways:
                                <ul className={s.ul}>
                                    <li>
                                        <strong>Easiest:</strong> give them{" "}
                                        <code>paymentPageUrl</code> — our ready-made page with
                                        the QR, amount and a timer.
                                    </li>
                                    <li>
                                        <strong>Your own page:</strong> embed the QR as an
                                        image — <code>{'<img src="{qrDataUri}">'}</code>. Or
                                        render the QR yourself from the <code>payload</code> string.
                                    </li>
                                </ul>
                            </li>
                            <li>
                                The customer scans the QR in their bank app and pays.
                            </li>
                            <li>
                                Get the result: we POST to your <code>callback_url</code>{" "}
                                (if provided), and/or poll{" "}
                                <code>GET /npay/payments/{"<transactionId>"}</code>.
                            </li>
                            <li>
                                Refund: <code>POST /npay/refunds</code> with{" "}
                                <code>transaction_id</code> and <code>amount</code> (also in rubles).
                            </li>
                        </ol>
                    ) : (
                        <ol className={s.ol}>
                            <li>
                                Creating payouts (through our adapter):
                                <ul className={s.ul}>
                                    <li>
                                        card: <code>POST /fins/payouts/card</code>
                                    </li>
                                    <li>
                                        SBP: <code>POST /fins/payouts/sbp</code>
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Idempotency is handled inside the adapter;{" "}
                                <code>X-Idempotency-Key</code> is not required. Use{" "}
                                <code>external_reference</code> for your own duplicates.
                            </li>
                            <li>
                                In <code>recipient</code> you MUST specify:{" "}
                                <code>last_name</code>, <code>first_name</code>,{" "}
                                <code>type</code>, <code>document_number</code>,{" "}
                                <code>document_date</code> and <code>middle_name</code>{" "}
                                <em>or</em> <code>has_middle_name=false</code> (if there is no
                                middle name).
                            </li>
                            <li>
                                Status check:
                                <br />
                                <code>
                                    GET /fins/payouts/status/&lt;transactionId|payoutId&gt;
                                </code>
                            </li>
                            <li>
                                Balance check: <code>GET /fins/balance</code>.
                            </li>
                        </ol>
                    )}
                </section>

                {/* Request body */}
                <section className={s.card}>
                    <h3 className={s.h3}>Request body</h3>

                    {providerKey === "craft" ? (
                        <div>
                            <h4 className={s.h4}>Required fields</h4>
                            <ul className={s.ul}>
                                <li><code>client</code> — client identifier (phone, email, etc.)</li>
                                <li><code>product</code> — product/service name</li>
                                <li><code>sum</code> — amount in rubles</li>
                            </ul>

                            <h4 className={s.h4}>Optional fields</h4>
                            <ul className={s.ul}>
                                <li><code>h2h</code> — <code>true</code> to get QR link, otherwise redirect</li>
                                <li><code>description</code> — payment description</li>
                                <li><code>externalReference</code> — your external order ID</li>
                                <li><code>callbackUrl</code> — URL for status notifications</li>
                                <li><code>redirectUrlSuccess</code> — success redirect URL (for Widget)</li>
                                <li><code>redirectUrlError</code> — error redirect URL (for Widget)</li>
                            </ul>

                            <Code lang="json">
                                {method ? method.bodyWithoutIp(method.label) : "{}"}
                            </Code>
                        </div>
                    ) : providerKey === "hydra" ? (
                        <div>
                            <p className={s.note}>
                                Currency <code>RUB</code>. <code>amount</code> is an integer in
                                rubles.
                                <br /> <code>successUrl</code>, <code>declineUrl</code>,{" "}
                                <code>callbackUrl</code> are optional.
                            </p>

                            <h4 className={s.h4}>
                                Option A with <code>customerIp</code>
                            </h4>
                            <div className={s.note}>
                                Returns <code>externalRefs</code> and identifiers.
                            </div>
                            <Code lang="json">
                                {method ? method.bodyWithIp(method.label) : "{}"}
                            </Code>

                            <h4 className={s.h4}>
                                Option B without <code>customerIp</code>
                            </h4>
                            <div className={s.note}>
                                Returns a payment link (<code>redirectUrl</code> or{" "}
                                <code>paymentUrl</code>).
                            </div>
                            <Code lang="json">
                                {method ? method.bodyWithoutIp(method.label) : "{}"}
                            </Code>
                        </div>
                    ) : providerKey === "tkb" ? (
                        <div>
                            <p className={s.note}>
                                All operations go through <code>POST /api/v1</code>. The body
                                must contain <code>method.type</code>. For payouts use{" "}
                                <code>
                                    {"{ method: { type: \"payout\", provider: \"tkb\" } }"}
                                </code>
                                .
                            </p>
                            <Code lang="json">
                                {method ? method.bodyWithoutIp(method.label) : "{}"}
                            </Code>
                        </div>
                    ) : providerKey === "npay" ? (
                        <div>
                            {methodKey === "payment_status" || methodKey === "refund_status" ? (
                                <p className={s.note}>
                                    GET request — no body needed. Pass{" "}
                                    <code>transactionId</code> in the URL.
                                </p>
                            ) : methodKey === "page" ? (
                                <p className={s.note}>
                                    <strong>Public</strong> GET request — <strong>no</strong>{" "}
                                    auth headers. <code>token</code> in the URL is the last
                                    segment of <code>paymentPageUrl</code> from the
                                    create-payment response. The same endpoint is used by our
                                    payment page. It does <strong>not</strong> work with the
                                    numeric <code>transactionId</code> (protection against
                                    guessing other merchants&apos; payments).
                                </p>
                            ) : (
                                <div>
                                    <p className={s.note}>
                                        <code>amount</code> is in <strong>rubles</strong>{" "}
                                        (number, e.g. <code>100</code> or <code>100.50</code>).
                                        We convert it to soms; <code>currency</code> in the
                                        response is <code>RUB</code>.
                                        {methodKey === "payment" && (
                                            <span> <code>redirect_url</code>,{" "}
                                            <code>callback_url</code>, <code>comment</code>,{" "}
                                            <code>expire_at</code>,{" "}
                                            <code>amount_can_be_changed</code> are optional
                                            (link lifetime defaults to 30 minutes).</span>
                                        )}
                                        {methodKey === "refund" && (
                                            <span> <code>transaction_id</code> is required
                                            (the original payment id). <code>comment</code>,{" "}
                                            <code>callback_url</code> are optional.</span>
                                        )}
                                    </p>
                                    <Code lang="json">
                                        {method && method.bodyWithoutIp ? method.bodyWithoutIp(method.label) : "{}"}
                                    </Code>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div>
                            <p className={s.note}>
                                Currency <code>RUB</code>. <code>amount</code> — value in
                                rubles. <code>external_reference</code> — string for your own
                                correlation.
                                <br />
                                For the recipient the following are required:{" "}
                                <code>last_name</code>, <code>first_name</code>,{" "}
                                <code>type</code>, <code>document_number</code>,{" "}
                                <code>document_date</code> and <code>middle_name</code>{" "}
                                <em>or</em> <code>has_middle_name=false</code>. Fields{" "}
                                <code>inn</code>, <code>purpose</code>, <code>reason</code>,{" "}
                                <code>email</code>, <code>phone</code> are optional (for SBP,
                                phone is required in <code>sbp.phone</code>).{" "}
                                <code>document_date</code> allows formats{" "}
                                <code>YYYY-MM-DD</code> and <code>DD.MM.YYYY</code>.
                            </p>
                            <Code lang="json">
                                {method ? method.bodyWithoutIp(method.label) : "{}"}
                            </Code>
                        </div>
                    )}
                </section>

                {/* Responses and cURL examples */}
                <section className={s.grid2}>
                    <div className={s.card}>
                        <h3 className={s.h3}>Successful response</h3>
                        {providerKey === "npay" ? (
                            <div>
                                <Code lang="json">{method ? method.successManual : "{}"}</Code>
                                {(methodKey === "payment" || methodKey === "refund") && createCurlWithoutIp && (
                                    <div>
                                        <h4 className={s.h4}>cURL example</h4>
                                        <Code lang="bash">{createCurlWithoutIp}</Code>
                                    </div>
                                )}
                                {(methodKey === "payment_status" || methodKey === "refund_status") && statusCurl && (
                                    <div>
                                        <h4 className={s.h4}>cURL example</h4>
                                        <Code lang="bash">{statusCurl}</Code>
                                    </div>
                                )}
                                {methodKey === "page" && npayPageCurl && (
                                    <div>
                                        <h4 className={s.h4}>cURL example</h4>
                                        <Code lang="bash">{npayPageCurl}</Code>
                                    </div>
                                )}
                            </div>
                        ) : providerKey === "hydra" ? (
                            <div>
                                <h4 className={s.h4}>
                                    If <code>customerIp</code> is provided (manual)
                                </h4>
                                <Code lang="json">{method ? method.successManual : "{}"}</Code>
                                <h4 className={s.h4}>If not provided (redirect)</h4>
                                <Code lang="json">{method ? method.successLink : "{}"}</Code>

                                <h4 className={s.h4}>cURL example</h4>
                                <div className={s.note}>
                                    Option A with <code>customerIp</code>
                                </div>
                                <Code lang="bash">{createCurlWithIp}</Code>
                                <div className={s.note}>
                                    Option B without <code>customerIp</code>
                                </div>
                                <Code lang="bash">{createCurlWithoutIp}</Code>
                            </div>
                        ) : (
                            <div>
                                <Code lang="json">{method ? method.successManual : "{}"}</Code>
                                <h4 className={s.h4}>cURL example</h4>
                                <Code lang="bash">{createCurlWithoutIp}</Code>
                            </div>
                        )}
                    </div>

                    <div className={s.card}>
                        <h3 className={s.h3}>Errors</h3>
                        {provider && provider.errorSamples ? (
                            <Code lang="json">{provider.errorSamples}</Code>
                        ) : (
                            <Code lang="json">{`// 400 Bad Request — invalid/missing fields
{ "message": "error explanation" }

// 401 or 403 — authentication errors`}</Code>
                        )}
                    </div>
                </section>

                {/* Balance check (only FINS renders separately; for TKB balance — via POST /v1) */}
                {providerKey === "fins" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Balance check</h2>
                        <p className={s.note}>
                            Aggregated CARD+SBP balance. Fields{" "}
                            <code>amount_minor</code> / <code>overdraft_minor</code> are in
                            minor units (kopecks). For humans —{" "}
                            <code>amount</code>/<code>overdraft</code> in rubles (2 decimals).
                        </p>
                        <h4 className={s.h4}>Sample response</h4>
                        <Code lang="json">{`{
  "ok": true,
  "provider": "finstar",
  "balance": {
    "amount": "123.45",
    "overdraft": "0.00",
    "currency": "RUB",
    "amount_minor": 12345,
    "overdraft_minor": 0
  },
  "request_id": "..."
}`}</Code>
                        <h4 className={s.h4}>cURL example</h4>
                        <Code lang="bash">
                            {buildCurlGET(
                                PROVIDERS.fins.baseUrlTest,
                                "/fins/balance",
                                PROVIDERS.fins.headers
                            )}
                        </Code>
                    </section>
                )}

                {/* Status check */}
                {provider && provider.supportsStatus && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Status check</h2>

                        {providerKey === "craft" ? (
                            <div>
                                <p className={s.note}>
                                    <code>GET {PROVIDERS.craft.status.path("{transactionId}")}</code>
                                </p>
                                <h4 className={s.h4}>Statuses: <code>PROCESSING</code>, <code>SUCCESS</code>, <code>DECLINED</code></h4>
                                <Code lang="json">{PROVIDERS.craft.status.success}</Code>
                                <h4 className={s.h4}>cURL</h4>
                                <Code lang="bash">{statusCurl}</Code>
                            </div>
                        ) : providerKey === "hydra" ? (
                            <div>
                                <p className={s.note}>
                                    Route{" "}
                                    <code>
                                        GET {PROVIDERS.hydra.status.path("{transactionId}")}
                                    </code>
                                    <br /> Auth headers are the same as for creation.
                                </p>
                                <h4 className={s.h4}>Sample response</h4>
                                <Code lang="json">{PROVIDERS.hydra.status.success}</Code>
                                <h4 className={s.h4}>cURL example</h4>
                                <Code lang="bash">{statusCurl}</Code>
                            </div>
                        ) : providerKey === "npay" ? (
                            <div>
                                <p className={s.note}>
                                    Payment: <code>GET {PROVIDERS.npay.status.path("{transactionId}")}</code>
                                    <br />
                                    Refund: <code>GET /npay/refunds/{"<transactionId>"}</code>
                                    <br />
                                    Same auth headers.
                                </p>
                                <h4 className={s.h4}>Sample response</h4>
                                <Code lang="json">{PROVIDERS.npay.status.success}</Code>
                                <h4 className={s.h4}>Statuses</h4>
                                <Code lang="json">{`// status (ours)  <- providerStatus (NPay)
"SUCCESS"        <- "COMPLETED"
"DECLINED"       <- "CANCELED" / "FAILED" / "EXPIRED"
"PENDING"        <- "ACTIVE" / "NEW"
"PROCESSING"     <- payment just created`}</Code>
                            </div>
                        ) : (
                            <div>
                                <p className={s.note}>
                                    Single route:
                                    <br />
                                    <code>
                                        GET{" "}
                                        {PROVIDERS.fins.status.path("{transactionId|payoutId}")}
                                    </code>
                                </p>
                                <h4 className={s.h4}>Sample response</h4>
                                <Code lang="json">{PROVIDERS.fins.status.success}</Code>
                                <h4 className={s.h4}>cURL example</h4>
                                <Code lang="bash">{statusCurl}</Code>
                            </div>
                        )}
                    </section>
                )}

                {/* Callback */}
                {(providerKey === "craft" ||
                    providerKey === "hydra" ||
                    providerKey === "tkb" ||
                    providerKey === "npay" ||
                    providerKey === "fins") && (
                    <section className={s.card}>
                        <h3 className={s.h3}>
                            Callback (if <code>callbackUrl</code> is set)
                        </h3>
                        <Code lang="json">
                            {providerKey === "craft"
                                ? PROVIDERS.craft.callbackSample
                                : providerKey === "hydra"
                                    ? PROVIDERS.hydra.callbackSample
                                    : providerKey === "tkb"
                                        ? PROVIDERS.tkb.callbackSample
                                        : providerKey === "npay"
                                            ? PROVIDERS.npay.callbackSample
                                            : PROVIDERS.fins.callbackSample}
                        </Code>
                        {providerKey === "npay" && PROVIDERS.npay.webhookSample && (
                            <div>
                                <h4 className={s.h4}>How the status is updated (reference)</h4>
                                <Code lang="json">{PROVIDERS.npay.webhookSample}</Code>
                            </div>
                        )}
                        <div className={s.note}>
                            POST JSON to your callback{providerKey === "tkb" ? "." : " with retries on network/5xx errors."}
                            {providerKey === "tkb" && (
                                <span>
                                    {" "}Only HTTP 200 counts as delivered: any other status, a timeout or a broken
                                    connection is retried with growing pauses - up to 24 attempts over about 31 hours.
                                </span>
                            )}
                            {providerKey === "payouts" && (
                                <span>
                                    {" "}Network errors, 5xx, 408 and 429 are retried for up to three days; other 4xx are treated as final.
                                </span>
                            )}
                            {providerKey === "payouts" && (
                                <span>
                                    {" "}Signature in header <Kbd>Sign</Kbd>: HMAC-SHA256 of body, key is your merchant key.
                                </span>
                            )}
                        </div>
                    </section>
                )}

                {/* Payment statuses */}
                {providerKey === "craft" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Payment statuses (pspState)</h2>
                        <p className={s.note}>
                            Statuses returned in the <code>pspState</code> field when checking payment status (<code>GET /craft/status/{"{id}"}</code>) or in webhook callback:
                        </p>
                        <table className={s.table}>
                            <thead>
                                <tr>
                                    <th>Status</th>
                                    <th>Type</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>SUCCESS</code></td>
                                    <td>Final</td>
                                    <td>Payment completed successfully</td>
                                </tr>
                                <tr>
                                    <td><code>DECLINED</code></td>
                                    <td>Final</td>
                                    <td>Payment declined or cancelled</td>
                                </tr>
                                <tr>
                                    <td><code>PROCESSING</code></td>
                                    <td>Intermediate</td>
                                    <td>Payment is being processed, awaiting confirmation</td>
                                </tr>
                                <tr>
                                    <td><code>ERROR</code></td>
                                    <td>Final</td>
                                    <td>Error during payment processing</td>
                                </tr>
                                <tr>
                                    <td><code>ALREADY_EXISTS</code></td>
                                    <td>Error</td>
                                    <td>Payment with this ID has already been submitted (duplicate)</td>
                                </tr>
                                <tr>
                                    <td><code>NOT_FOUND</code></td>
                                    <td>Critical error</td>
                                    <td>Payment not found in the provider system</td>
                                </tr>
                                <tr>
                                    <td><code>null</code></td>
                                    <td>No data</td>
                                    <td>Status unavailable, no information from provider</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                )}

                {providerKey === "hydra" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Transaction statuses</h2>
                        <p className={s.note}>
                            Statuses that may be returned in <code>state</code> / <code>hydraState</code> fields:
                        </p>
                        <table className={s.table}>
                            <thead>
                                <tr>
                                    <th>Status</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>PENDING</code></td>
                                    <td>Payment created, awaiting payment</td>
                                </tr>
                                <tr>
                                    <td><code>PROCESSING</code></td>
                                    <td>Payment is being processed</td>
                                </tr>
                                <tr>
                                    <td><code>COMPLETED</code></td>
                                    <td>Payment completed successfully</td>
                                </tr>
                                <tr>
                                    <td><code>AUTHORIZED</code></td>
                                    <td>Payment authorized (success)</td>
                                </tr>
                                <tr>
                                    <td><code>DECLINED</code></td>
                                    <td>Payment declined</td>
                                </tr>
                                <tr>
                                    <td><code>CANCELED</code></td>
                                    <td>Payment cancelled by user or system</td>
                                </tr>
                                <tr>
                                    <td><code>EXPIRED</code></td>
                                    <td>Payment time expired</td>
                                </tr>
                                <tr>
                                    <td><code>ERROR</code></td>
                                    <td>Error during payment processing</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                )}

                {providerKey === "tkb" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Transaction statuses</h2>
                        <p className={s.note}>
                            Statuses that may be returned in the <code>transactionType</code> field:
                        </p>
                        <table className={s.table}>
                            <thead>
                                <tr>
                                    <th>Status</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>PAYOUT_CREATED</code></td>
                                    <td>Payout created, awaiting processing</td>
                                </tr>
                                <tr>
                                    <td><code>PAYOUT_PENDING</code></td>
                                    <td>Payout is being processed</td>
                                </tr>
                                <tr>
                                    <td><code>PAYOUT</code></td>
                                    <td>Payout completed successfully</td>
                                </tr>
                                <tr>
                                    <td><code>PAYOUT_DECLINE</code></td>
                                    <td>Payout declined</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                )}

                {providerKey === "fins" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Transaction statuses</h2>
                        <p className={s.note}>
                            Normalized statuses in the <code>status</code> field of the response:
                        </p>
                        <table className={s.table}>
                            <thead>
                                <tr>
                                    <th>Status</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>success</code></td>
                                    <td>Payout completed successfully</td>
                                </tr>
                                <tr>
                                    <td><code>pending</code></td>
                                    <td>Payout is being processed</td>
                                </tr>
                                <tr>
                                    <td><code>declined</code></td>
                                    <td>Payout declined</td>
                                </tr>
                            </tbody>
                        </table>
                        <p className={s.note} style={{ marginTop: "1rem" }}>
                            Original provider statuses (<code>last_operation_status</code> field):
                        </p>
                        <table className={s.table}>
                            <thead>
                                <tr>
                                    <th>Status</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr><td><code>approve</code></td><td>Operation approved (success)</td></tr>
                                <tr><td><code>approved</code></td><td>Operation approved (success)</td></tr>
                                <tr><td><code>pending</code></td><td>Operation awaiting processing</td></tr>
                                <tr><td><code>processing</code></td><td>Operation in progress</td></tr>
                                <tr><td><code>created</code></td><td>Operation created</td></tr>
                                <tr><td><code>decline</code></td><td>Operation declined</td></tr>
                                <tr><td><code>declined</code></td><td>Operation declined</td></tr>
                                <tr><td><code>failed</code></td><td>Execution error</td></tr>
                                <tr><td><code>canceled</code></td><td>Operation cancelled</td></tr>
                                <tr><td><code>expired</code></td><td>Operation time expired</td></tr>
                            </tbody>
                        </table>
                    </section>
                )}

                {providerKey === "payouts" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Request statuses</h2>
                        <p className={s.note}>
                            Statuses returned in the <code>status</code> / <code>statusCode</code> fields:
                        </p>
                        <table className={s.table}>
                            <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Status</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>0</code></td>
                                    <td><code>created</code></td>
                                    <td>Request created</td>
                                </tr>
                                <tr>
                                    <td><code>7</code></td>
                                    <td><code>awaitingPayout</code></td>
                                    <td>Awaiting payout (antifraud passed)</td>
                                </tr>
                                <tr>
                                    <td><code>3</code></td>
                                    <td><code>inRegistry</code></td>
                                    <td>Included in payout registry</td>
                                </tr>
                                <tr>
                                    <td><code>4</code></td>
                                    <td><code>processing</code></td>
                                    <td>Payout is being processed</td>
                                </tr>
                                <tr>
                                    <td><code>5</code></td>
                                    <td><code>success</code></td>
                                    <td>Payout completed successfully</td>
                                </tr>
                                <tr>
                                    <td><code>6</code></td>
                                    <td><code>failed</code></td>
                                    <td>Payout failed</td>
                                </tr>
                                <tr>
                                    <td><code>1</code></td>
                                    <td><code>antifraudDeclined</code></td>
                                    <td>Blocked by antifraud</td>
                                </tr>
                                <tr>
                                    <td><code>2</code></td>
                                    <td><code>declined</code></td>
                                    <td>Declined</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                )}

                {providerKey === "payouts" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Authentication</h2>
                        <p className={s.note}>
                            Two authentication methods are supported:
                        </p>
                        <h4 className={s.h4}>Method 1: Simple authentication</h4>
                        <ul className={s.ul}>
                            <li><code>X-Merchant-UUID</code> — Merchant UUID</li>
                            <li><code>X-Merchant-Key</code> — Merchant API key</li>
                        </ul>
                        <h4 className={s.h4}>Method 2: HMAC signature</h4>
                        <ul className={s.ul}>
                            <li><code>Merchant</code> — Merchant UUID</li>
                            <li><code>Sign</code> — HMAC-SHA256 signature of the request body with your key</li>
                        </ul>
                        <Code lang="bash">{`# Signature generation example
SIGN=$(echo -n '$JSON_BODY' | openssl dgst -sha256 -hmac '$MERCHANT_KEY' | awk '{print $2}')
curl -X POST 'https://api.e-clear.cc/api/v2/payout-request' \\
  -H 'Content-Type: application/json' \\
  -H 'Merchant: <YOUR-UUID>' \\
  -H 'Sign: '$SIGN \\
  -d '$JSON_BODY'`}</Code>
                    </section>
                )}

                {providerKey === "fins" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Provider error codes (4PMTS)</h2>
                        <p className={s.note}>
                            When a payout is declined, the provider returns a numeric <code>error_code</code> and text description.
                            Below is the complete reference of codes grouped by category.
                            The error code is returned in the <code>error.details.error_code</code> field of the response.
                        </p>

                        <h3 className={s.h3}>System errors (100–114)</h3>
                        <table className={s.table}>
                            <thead><tr><th>Code</th><th>Description</th></tr></thead>
                            <tbody>
                                <tr><td><code>100</code></td><td>Unknown system error</td></tr>
                                <tr><td><code>101</code></td><td>Resource not found (transaction/payout does not exist)</td></tr>
                                <tr><td><code>102</code></td><td>Operation unavailable for this order</td></tr>
                                <tr><td><code>103</code></td><td>Method not allowed for this order</td></tr>
                                <tr><td><code>104</code></td><td>Duplicate request (idempotency key)</td></tr>
                                <tr><td><code>105</code></td><td>Input parameter validation error</td></tr>
                                <tr><td><code>106</code></td><td>Invalid request format</td></tr>
                                <tr><td><code>107</code></td><td>Rate limit exceeded</td></tr>
                                <tr><td><code>108</code></td><td>Authorization error (invalid token/key)</td></tr>
                                <tr><td><code>109</code></td><td>Access denied (no permissions for operation)</td></tr>
                                <tr><td><code>110</code></td><td>Operation temporarily unavailable (maintenance)</td></tr>
                                <tr><td><code>111</code></td><td>Provider request timeout</td></tr>
                                <tr><td><code>112</code></td><td>Provider connection error</td></tr>
                                <tr><td><code>113</code></td><td>Internal processing error</td></tr>
                                <tr><td><code>114</code></td><td>Unsupported currency</td></tr>
                            </tbody>
                        </table>

                        <h3 className={s.h3}>Antifraud (200–280)</h3>
                        <table className={s.table}>
                            <thead><tr><th>Code</th><th>Description</th></tr></thead>
                            <tbody>
                                <tr><td><code>200</code></td><td>Transaction declined by antifraud system</td></tr>
                                <tr><td><code>201</code></td><td>Suspected fraud</td></tr>
                                <tr><td><code>202</code></td><td>Card is blacklisted</td></tr>
                                <tr><td><code>203</code></td><td>IP address is blacklisted</td></tr>
                                <tr><td><code>204</code></td><td>Card country not allowed</td></tr>
                                <tr><td><code>205</code></td><td>Amount limit exceeded</td></tr>
                                <tr><td><code>206</code></td><td>Transaction count limit exceeded</td></tr>
                                <tr><td><code>207</code></td><td>Card expired</td></tr>
                                <tr><td><code>210</code></td><td>Declined by 3D-Secure check</td></tr>
                                <tr><td><code>250</code></td><td>Invalid recipient data</td></tr>
                                <tr><td><code>260</code></td><td>Recipient is on sanctions list</td></tr>
                                <tr><td><code>270</code></td><td>Recipient bank not supported</td></tr>
                                <tr><td><code>280</code></td><td>Recipient account closed or blocked</td></tr>
                            </tbody>
                        </table>

                        <h3 className={s.h3}>3D-Secure (300–308)</h3>
                        <table className={s.table}>
                            <thead><tr><th>Code</th><th>Description</th></tr></thead>
                            <tbody>
                                <tr><td><code>300</code></td><td>3D-Secure authentication error</td></tr>
                                <tr><td><code>301</code></td><td>3D-Secure unavailable</td></tr>
                                <tr><td><code>302</code></td><td>User failed 3D-Secure</td></tr>
                                <tr><td><code>303</code></td><td>3D-Secure timeout</td></tr>
                                <tr><td><code>304</code></td><td>3D-Secure redirect error</td></tr>
                                <tr><td><code>305</code></td><td>Invalid 3DS server response</td></tr>
                                <tr><td><code>306</code></td><td>3DS required but not supported by card</td></tr>
                                <tr><td><code>307</code></td><td>Declined by issuer during 3DS check</td></tr>
                                <tr><td><code>308</code></td><td>3DS verification not completed</td></tr>
                            </tbody>
                        </table>

                        <h3 className={s.h3}>Provider/bank errors (400–503)</h3>
                        <table className={s.table}>
                            <thead><tr><th>Code</th><th>Description</th></tr></thead>
                            <tbody>
                                <tr><td><code>400</code></td><td>General provider error</td></tr>
                                <tr><td><code>401</code></td><td>Insufficient funds on sender account</td></tr>
                                <tr><td><code>402</code></td><td>Transaction declined by bank</td></tr>
                                <tr><td><code>403</code></td><td>Card limit exceeded</td></tr>
                                <tr><td><code>404</code></td><td>Card not found / invalid card number</td></tr>
                                <tr><td><code>405</code></td><td>Card blocked</td></tr>
                                <tr><td><code>406</code></td><td>Card expired</td></tr>
                                <tr><td><code>407</code></td><td>Invalid CVV/CVC</td></tr>
                                <tr><td><code>408</code></td><td>Card type not supported</td></tr>
                                <tr><td><code>409</code></td><td>Issuing bank unavailable</td></tr>
                                <tr><td><code>410</code></td><td>Duplicate transaction</td></tr>
                                <tr><td><code>411</code></td><td>Invalid transaction amount</td></tr>
                                <tr><td><code>412</code></td><td>Invalid currency</td></tr>
                                <tr><td><code>420</code></td><td>Provider technical error</td></tr>
                                <tr><td><code>430</code></td><td>Acquirer bank response timeout</td></tr>
                                <tr><td><code>440</code></td><td>Operation not supported by terminal</td></tr>
                                <tr><td><code>450</code></td><td>Insufficient funds for payout</td></tr>
                                <tr><td><code>500</code></td><td>Internal payment service error</td></tr>
                                <tr><td><code>501</code></td><td>Service temporarily unavailable</td></tr>
                                <tr><td><code>502</code></td><td>Gateway error</td></tr>
                                <tr><td><code>503</code></td><td>Service overloaded</td></tr>
                            </tbody>
                        </table>

                        <h3 className={s.h3}>Issuer bank responses (601–699)</h3>
                        <table className={s.table}>
                            <thead><tr><th>Code</th><th>Description</th></tr></thead>
                            <tbody>
                                <tr><td><code>601</code></td><td>Issuer declined without explanation</td></tr>
                                <tr><td><code>602</code></td><td>Suspected fraud (issuer)</td></tr>
                                <tr><td><code>603</code></td><td>Card restricted for this type of operation</td></tr>
                                <tr><td><code>604</code></td><td>Card seized / lost / stolen</td></tr>
                                <tr><td><code>605</code></td><td>Invalid PIN</td></tr>
                                <tr><td><code>606</code></td><td>PIN attempt limit exceeded</td></tr>
                                <tr><td><code>607</code></td><td>Cardholder should contact issuer</td></tr>
                                <tr><td><code>610</code></td><td>Amount exceeds allowed limit</td></tr>
                                <tr><td><code>620</code></td><td>Insufficient funds</td></tr>
                                <tr><td><code>630</code></td><td>Account closed</td></tr>
                                <tr><td><code>640</code></td><td>Transaction declined: country restrictions</td></tr>
                                <tr><td><code>650</code></td><td>Invalid card details</td></tr>
                                <tr><td><code>660</code></td><td>Issuer response timeout</td></tr>
                                <tr><td><code>699</code></td><td>Other issuer decline</td></tr>
                            </tbody>
                        </table>

                        <h3 className={s.h3}>Extended codes (720–779)</h3>
                        <table className={s.table}>
                            <thead><tr><th>Code</th><th>Description</th></tr></thead>
                            <tbody>
                                <tr><td><code>720</code></td><td>Payment routing error</td></tr>
                                <tr><td><code>721</code></td><td>Invalid recurring payment parameters</td></tr>
                                <tr><td><code>722</code></td><td>Subscription cancelled</td></tr>
                                <tr><td><code>723</code></td><td>Card token is invalid</td></tr>
                                <tr><td><code>730</code></td><td>Refund not possible (period expired)</td></tr>
                                <tr><td><code>731</code></td><td>Partial refund not supported</td></tr>
                                <tr><td><code>740</code></td><td>Operation requires user confirmation</td></tr>
                                <tr><td><code>750</code></td><td>Invalid phone number format</td></tr>
                                <tr><td><code>760</code></td><td>Invalid email format</td></tr>
                                <tr><td><code>770</code></td><td>Invalid recipient personal data</td></tr>
                                <tr><td><code>779</code></td><td>Other validation errors</td></tr>
                            </tbody>
                        </table>

                        <h3 className={s.h3}>SBP codes (801–854)</h3>
                        <table className={s.table}>
                            <thead><tr><th>Code</th><th>Description</th></tr></thead>
                            <tbody>
                                <tr><td><code>801</code></td><td>Recipient bank is not an SBP participant</td></tr>
                                <tr><td><code>802</code></td><td>Recipient not found in SBP system</td></tr>
                                <tr><td><code>803</code></td><td>Recipient account not linked to SBP</td></tr>
                                <tr><td><code>804</code></td><td>SBP transfer timeout</td></tr>
                                <tr><td><code>805</code></td><td>SBP transfer declined by recipient bank</td></tr>
                                <tr><td><code>806</code></td><td>Invalid phone number format for SBP</td></tr>
                                <tr><td><code>807</code></td><td>Invalid bank_member_id</td></tr>
                                <tr><td><code>808</code></td><td>SBP transfer limit exceeded</td></tr>
                                <tr><td><code>810</code></td><td>SBP technical failure</td></tr>
                                <tr><td><code>820</code></td><td>SBP unavailable (maintenance)</td></tr>
                                <tr><td><code>830</code></td><td>Recipient restricted SBP transfers</td></tr>
                                <tr><td><code>840</code></td><td>Invalid SBP QR code parameters</td></tr>
                                <tr><td><code>850</code></td><td>Declined by SBP antifraud system</td></tr>
                                <tr><td><code>851</code></td><td>Suspicious operation (SBP)</td></tr>
                                <tr><td><code>852</code></td><td>Recipient is on SBP stop list</td></tr>
                                <tr><td><code>853</code></td><td>Amount below SBP minimum</td></tr>
                                <tr><td><code>854</code></td><td>Amount exceeds SBP maximum</td></tr>
                            </tbody>
                        </table>
                    </section>
                )}

                <footer className={s.footer}>
                    <span>© E-clear</span>
                    <span className={s.dot}>•</span>
                    <a className={s.link} href="#top">
                        Back to top
                    </a>
                </footer>
            </main>
        </div>
    );
};

export default MerchantsDocsEn;



// WEBPACK FOOTER //
// ./src/PagesOfCommon/wiki/index.en.js